import { useMemo } from 'react'

// Product formula (Figma TUTD "How it's calculated" panel). All inputs below
// are product-owned constants, not derived from any merchant data.
const COMPLAINT_RATE = 0.035
// Exported so callers that display the reclaimed GM *hours* alongside
// laborSavings (the RecoveryAI promo ROI breakdown) derive them from the same
// constant instead of restating it.
export const HANDLING_MINUTES_PER_COMPLAINT = 4
const GM_HOURLY_PAY_DOLLARS = 30
const RESOLVED_RATE = 0.75
const REVISIT_RATE = 0.3
// Placeholder until the roi_projection endpoint supplies a measured average
// ticket for the merchant — see recoveredRevenue below.
const DEFAULT_AVG_ORDER_VALUE = 24.57

// Re-expresses the old defaults' complaint volume (12 locations x 7
// complaints/week x 4.33 weeks/month) as a monthly order volume, so callers
// that must always show an estimate (e.g. the pre-activation RecoveryAI
// pitch) have a sensible fallback: (12 x 7 x 4.33) / 0.035 ≈ 10,400.
export const DEFAULT_MONTHLY_ORDERS = 10_400

export type ROIStats = {
  dissatisfiedGuests: number
  laborSavings: number
  recoveredRevenue: number
  totalMonthly: number
}

// Worked example: 73,200 monthly orders x 3.5% complaint rate = 2,562
// dissatisfied guests/month.
//   laborSavings = 2,562 x (4 min / 60) x $30/hr ≈ $5,124/month
//   recoveredRevenue = 2,562 x 75% resolved x 30% revisit x avg order value
export function useROICalculator(
  monthlyOrders: number | null | undefined
): ROIStats | null {
  return useMemo(() => {
    if (!monthlyOrders || monthlyOrders <= 0) return null

    const dissatisfiedGuests = Math.round(monthlyOrders * COMPLAINT_RATE)

    const laborSavings = Math.round(
      dissatisfiedGuests *
        (HANDLING_MINUTES_PER_COMPLAINT / 60) *
        GM_HOURLY_PAY_DOLLARS
    )

    // recoveredRevenue: KEEP DEFAULT_AVG_ORDER_VALUE as a placeholder until the
    // roi_projection endpoint supplies a measured average ticket for this
    // merchant.
    const recoveredRevenue = Math.round(
      dissatisfiedGuests *
        RESOLVED_RATE *
        REVISIT_RATE *
        DEFAULT_AVG_ORDER_VALUE
    )

    return {
      dissatisfiedGuests,
      laborSavings,
      recoveredRevenue,
      totalMonthly: laborSavings + recoveredRevenue,
    }
  }, [monthlyOrders])
}
